import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import {
  BANNER_DEGRADED,
  BANNER_UNREACHABLE,
  decideBannerVariant,
  trackConnectivityVariant,
} from '../../lib/network/connectivityBannerTiming';

const BANNER_HEIGHT_VAR = '--connectivity-banner-h';

function setBannerHeight(px) {
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, px ? `${px}px` : '0px');
}

/**
 * Subtle connectivity affordance — degraded vs offline.
 * Fixed below the app header; reserves space via --connectivity-banner-h only while visible.
 */
export default function ConnectivityBanner() {
  const { isEffectivelyDegraded, isEffectivelyUnreachable } = useNetworkStatus();
  const ref = useRef(null);
  const [visibleVariant, setVisibleVariant] = useState(null);
  const [recheck, setRecheck] = useState(0);
  const trackRef = useRef(null);

  const variant = isEffectivelyUnreachable
    ? BANNER_UNREACHABLE
    : isEffectivelyDegraded
      ? BANNER_DEGRADED
      : null;

  // Hysteresis lives in lib/network/connectivityBannerTiming (timings + rationale there). Display only.
  useEffect(() => {
    const now = Date.now();
    trackRef.current = trackConnectivityVariant(trackRef.current, variant, now);
    const { visible, recheckMs } = decideBannerVariant(visibleVariant, trackRef.current, now);
    if (visible !== visibleVariant) {
      setVisibleVariant(visible);
      return undefined;
    }
    if (!Number.isFinite(recheckMs)) return undefined;
    const timer = window.setTimeout(() => setRecheck((n) => n + 1), Math.max(0, recheckMs) + 20);
    return () => window.clearTimeout(timer);
  }, [variant, visibleVariant, recheck]);

  useLayoutEffect(() => {
    if (!visibleVariant) {
      setBannerHeight(0);
      return undefined;
    }

    const el = ref.current;
    if (!el) {
      setBannerHeight(0);
      return undefined;
    }

    const syncHeight = () => setBannerHeight(el.offsetHeight);
    syncHeight();

    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);

    return () => {
      ro.disconnect();
      setBannerHeight(0);
    };
  }, [visibleVariant]);

  if (!visibleVariant) {
    return null;
  }

  const className =
    visibleVariant === 'unreachable'
      ? 'border-b border-danger-ring bg-danger-fill text-danger'
      : 'border-b border-warn-ring bg-warn-fill text-warn';

  const message = visibleVariant === 'unreachable' ? 'Offline' : 'Slow connection';

  const dotClassName = visibleVariant === 'unreachable' ? 'status-dot--danger' : 'status-dot--warn';

  return (
    <div
      ref={ref}
      role="status"
      className={`connectivity-banner fixed inset-x-0 px-2 py-1.5 sm:px-3 ${className}`}
      style={{ top: 'var(--app-chrome-top)' }}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-1.5">
        <span
          className={`h-1.5 w-1.5 shrink-0 animate-pulse rounded-full ${dotClassName}`}
          aria-hidden="true"
        />
        <span className="text-xs font-medium leading-none">{message}</span>
      </div>
    </div>
  );
}
